import { IGetUserResponse, IResetUserPasswordRequest, ISetUserRoleRequest, IUserSettingRequest, UserSettingEntity } from './types';
import { baseSettingsApi, SettingItemEntity } from '@src/core/features';


export const settingApi = baseSettingsApi.injectEndpoints({
  endpoints: (builder) => ({
    setSettings: builder.mutation<void, SettingItemEntity[]>({
      query: (data) => ({
        url: '/api/v1/orders/settings',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Settings']
    }),
    getUsers: builder.query<IGetUserResponse, void>({
      query: () => ({
        url: '/api/v1/user',
        method: 'GET',
      }),
      providesTags: ['Users'],
    }),
    createUser: builder.mutation({
      query: (data) => ({
        url: '/api/v1/user',
        method: 'POST',
        data,
      })
    }),
    getUserRoles: builder.query<string[], void>({
      query: () => ({
        url: '/api/v1/user/role',
        method: 'GET',
      })
    }),
    setUserRole: builder.mutation<void, ISetUserRoleRequest>({
      query: (data) => ({
        url: '/api/v1/user/role',
        method: 'PUT',
        data
      }),
      invalidatesTags: ['Users'],
    }),
    resetUserPassword: builder.mutation<void, IResetUserPasswordRequest>({
      query: ({ id, data }) => ({
        url: `/api/v1/user/password/${id}`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Users'],
    }),
    deleteUser: builder.mutation<void, number>({
      query: (userId) => ({
        url: `/api/v1/user/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    // user-settings (key-value storage)
    getUserSetting: builder.query<UserSettingEntity, string>({
      query: (key) => ({
        url: `/api/v1/user/settings/${key}`,
        method: 'GET',
      }),
      transformResponse: (response: UserSettingEntity): UserSettingEntity => {
        try {
          const parsed = JSON.parse(response.value);
          if (typeof parsed === 'string') {
            return { ...response, value: parsed };
          }
        } catch { /* value is not JSON-encoded, use as-is */ }
        return response;
      },
      providesTags: (_result, _error, key) => [{ type: 'UserSettings' as const, id: key }],
    }),
    createUserSetting: builder.mutation<void, IUserSettingRequest>({
      query: ({ key, value }) => ({
        url: `/api/v1/user/settings/${key}`,
        method: 'POST',
        data: value,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'UserSettings' as const, id: key }],
    }),
    updateUserSetting: builder.mutation<void, IUserSettingRequest>({
      query: ({ key, value }) => ({
        url: `/api/v1/user/settings/${key}`,
        method: 'PUT',
        data: value,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'UserSettings' as const, id: key }],
    }),
    deleteUserSetting: builder.mutation<void, string>({
      query: (key) => ({
        url: `/api/v1/user/settings/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, key) => [{ type: 'UserSettings' as const, id: key }],
    }),
  })
});

export const { 
  useResetUserPasswordMutation,
  useSetSettingsMutation,
  useGetUsersQuery, 
  useGetUserRolesQuery,
  useCreateUserMutation,
  useSetUserRoleMutation,
  useDeleteUserMutation,

  useGetUserSettingQuery,
  useCreateUserSettingMutation,
  useUpdateUserSettingMutation,
  useDeleteUserSettingMutation,
} = settingApi;